import { format } from "date-fns"
import {
  HOUSEKEEPING_AREAS,
  isConsoleAppCode,
} from "@/v2-backoffice-client/usage/usage.constants"

/** One user × day × area × app row as the usage queries return it. */
export type UsageAreaDayRow = {
  Date: string
  AppArea: string
  /** Product the events came from; rows synced pre-split read 'v2-console'. */
  AppCode?: string | null
  EventCount: number
  WeightedCount?: number | null
}

/**
 * Filters daily rows to one app; null keeps every app. The chips above the
 * activity sections drive this.
 */
export function filterAreaDaysByApp(
  areaDays: UsageAreaDayRow[],
  appCode: string | null,
) {
  if (appCode == null) return areaDays
  return areaDays.filter((areaDay) => (areaDay.AppCode ?? null) === appCode)
}

/** Distinct app codes present in the daily rows, v1 before v2. */
/**
 * The consoles' rows only — what the Fusion tab is about. Rows synced before
 * the product split carry no AppCode and were console events.
 */
export function filterConsoleAreaDays(areaDays: UsageAreaDayRow[]) {
  return areaDays.filter(
    (areaDay) => areaDay.AppCode == null || isConsoleAppCode(areaDay.AppCode),
  )
}

export function getAreaDayApps(areaDays: UsageAreaDayRow[]) {
  return [
    ...new Set(
      areaDays
        .map((areaDay) => areaDay.AppCode)
        .filter((appCode): appCode is string => appCode != null),
    ),
  ].toSorted()
}

/**
 * One bucket per calendar day, all areas summed. Uses the weighted count
 * when present (rows synced before the weighting rollout carry only raw
 * counts) and keeps the raw count for the tooltip.
 */
export function deriveDailyTotals(areaDays: UsageAreaDayRow[]) {
  const byDay = new Map<string, { value: number; rawCount: number }>()
  for (const areaDay of areaDays) {
    const day = String(areaDay.Date).slice(0, 10)
    const entry = byDay.get(day) ?? { value: 0, rawCount: 0 }
    entry.value += areaDay.WeightedCount ?? areaDay.EventCount
    entry.rawCount += areaDay.EventCount
    byDay.set(day, entry)
  }
  return [...byDay.entries()]
    .toSorted(([a], [b]) => a.localeCompare(b))
    .map(([day, entry]) => ({ day, ...entry }))
}

export type UsageDailyTotal = ReturnType<typeof deriveDailyTotals>[number]

/**
 * One entry per calendar day over the trailing window, zero-filled: quiet
 * days must render as quiet days, otherwise a user with 4 active days shows
 * 4 giant bars and the history reads as constant activity. The window ends
 * at `end` (defaults to today) so historical windows — e.g. a past quarter
 * on the Portfolio — pad up to their own last day, not to now.
 */
export function padDailyTotals(
  entries: UsageDailyTotal[],
  days: number,
  end: Date = new Date(),
): UsageDailyTotal[] {
  const byDay = new Map(entries.map((entry) => [entry.day, entry]))
  return Array.from({ length: days }, (_, i) => {
    const date = new Date(end.getTime() - (days - 1 - i) * DAY_MS)
    const day = date.toISOString().slice(0, 10)
    return byDay.get(day) ?? { day, value: 0, rawCount: 0 }
  })
}

export const DAY_MS = 24 * 60 * 60 * 1000
const AREA_LIMIT = 6

/**
 * Interest areas by weighted 28-day volume with a per-area trend (weighted
 * 28d vs the 28 before), highest volume first. Housekeeping areas
 * (search/account/support) count toward volume elsewhere but say nothing
 * about interests, so they are excluded here.
 */
/**
 * Per-app weighted volume over the trailing 28 days with a 28d-vs-prev-28d
 * trend and the share of the total, dominant app first. Exact windows from
 * the daily rows — this is the number behind the product-mix split bars.
 */
export function deriveAppSplit(areaDays: UsageAreaDayRow[], anchor?: Date) {
  // The 28d/56d windows hang off `anchor` (defaults to now) so historical
  // windows — e.g. a past quarter on the Portfolio — compare the LAST 28
  // days of that window against the 28 before, not empty now-relative ones.
  const end = anchor?.getTime() ?? Date.now()
  const from28d = new Date(end - 28 * DAY_MS)
  const from56d = new Date(end - 56 * DAY_MS)

  const byApp = new Map<string, { current: number; previous: number }>()
  for (const areaDay of areaDays) {
    const appCode = areaDay.AppCode
    if (appCode == null) continue
    const date = new Date(String(areaDay.Date).slice(0, 10))
    if (date < from56d) continue
    const entry = byApp.get(appCode) ?? { current: 0, previous: 0 }
    const value = areaDay.WeightedCount ?? areaDay.EventCount
    if (date >= from28d) entry.current += value
    else entry.previous += value
    byApp.set(appCode, entry)
  }

  const total = [...byApp.values()].reduce(
    (sum, entry) => sum + entry.current,
    0,
  )
  return [...byApp.entries()]
    .map(([app, { current, previous }]) => ({
      app,
      current,
      previous,
      trendPct: previous > 0 ? ((current - previous) / previous) * 100 : null,
      share: total > 0 ? (current / total) * 100 : 0,
    }))
    .filter((entry) => entry.current > 0 || entry.previous > 0)
    .sort((a, b) => b.current - a.current)
}

export type UsageAppSplitEntry = ReturnType<typeof deriveAppSplit>[number]

export function deriveAreaBreakdown(
  areaDays: UsageAreaDayRow[],
  anchor?: Date,
  /** Rows to keep, biggest first; pass Infinity to count every used area. */
  limit: number = AREA_LIMIT,
) {
  // Same anchor semantics as deriveAppSplit above.
  const end = anchor?.getTime() ?? Date.now()
  const from28d = new Date(end - 28 * DAY_MS)
  const from56d = new Date(end - 56 * DAY_MS)

  const byArea = new Map<string, { current: number; previous: number }>()
  for (const areaDay of areaDays) {
    if (HOUSEKEEPING_AREAS.has(areaDay.AppArea)) continue
    const date = new Date(String(areaDay.Date).slice(0, 10))
    if (date < from56d) continue
    const entry = byArea.get(areaDay.AppArea) ?? { current: 0, previous: 0 }
    const value = areaDay.WeightedCount ?? areaDay.EventCount
    if (date >= from28d) entry.current += value
    else entry.previous += value
    byArea.set(areaDay.AppArea, entry)
  }

  return [...byArea.entries()]
    .map(([area, { current, previous }]) => ({
      area,
      current,
      previous,
      trendPct: previous > 0 ? ((current - previous) / previous) * 100 : null,
    }))
    .filter((entry) => entry.current > 0)
    .sort((a, b) => b.current - a.current)
    .slice(0, limit)
}

export type UsageWeekGridRow = {
  /** ISO date of the Monday. */
  weekStart: string
  /** Seven slots, Monday first; null where the series has no day. */
  days: ({ date: string; value: number } | null)[]
}

/** Monday-first weekday index (0–6) of a date. */
function mondayIndex(date: Date) {
  return (date.getDay() + 6) % 7
}

/** Local calendar date as YYYY-MM-DD (toISOString would shift it to UTC). */
function localIsoDate(date: Date) {
  return format(date, "yyyy-MM-dd")
}

/**
 * Lays a day series (oldest first, the last one today) out as calendar
 * rows, Monday to Sunday, for the week grid.
 */
export function weekGridRows(series: number[]): UsageWeekGridRow[] {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const rows: UsageWeekGridRow[] = []
  for (let i = 0; i < series.length; i += 1) {
    const date = new Date(today.getTime() - (series.length - 1 - i) * DAY_MS)
    const column = mondayIndex(date)
    const monday = new Date(date.getTime() - column * DAY_MS)
    const weekStart = localIsoDate(monday)
    let row = rows[rows.length - 1]
    if (!row || row.weekStart !== weekStart) {
      row = { weekStart, days: Array.from({ length: 7 }, () => null) }
      rows.push(row)
    }
    row.days[column] = {
      date: localIsoDate(date),
      value: series[i],
    }
  }
  return rows
}

/**
 * Weekday and weekend averages of a day series ending today — the sentence
 * under the week grid ("weekdays average 480 people, weekends 61").
 */
export function deriveWeekRhythm(series: number[]) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  let weekdaySum = 0
  let weekdayCount = 0
  let weekendSum = 0
  let weekendCount = 0
  let peak = 0
  let peakDate: string | null = null
  for (let i = 0; i < series.length; i += 1) {
    const date = new Date(today.getTime() - (series.length - 1 - i) * DAY_MS)
    if (mondayIndex(date) >= 5) {
      weekendSum += series[i]
      weekendCount += 1
    } else {
      weekdaySum += series[i]
      weekdayCount += 1
    }
    if (series[i] > peak) {
      peak = series[i]
      peakDate = localIsoDate(date)
    }
  }
  return {
    weekdayAverage: weekdayCount > 0 ? weekdaySum / weekdayCount : 0,
    weekendAverage: weekendCount > 0 ? weekendSum / weekendCount : 0,
    peak,
    peakDate,
  }
}
